/**
 * Enhanced survey question types for Phase 2
 * Makes existing survey functionality data-driven
 */

/**
 * Enhanced survey question from backend API
 * Supports scale (Likert) and text response types
 */
export interface EnhancedSurveyQuestion {
  // Core fields (existing)
  id: number;
  surveyId: number;
  text: string;
  order: number;
  
  // Phase 2 fields
  responseType: 'scale' | 'text';
  
  // Scale configuration (DEPRECATED - always use 1-5 with standard labels)
  scaleMin?: number;         // DEPRECATED - always 1
  scaleMax?: number;         // DEPRECATED - always 5
  scaleMinLabel?: string;    // DEPRECATED - ignored
  scaleMaxLabel?: string;    // DEPRECATED - ignored
  
  // Text configuration (for open-ended questions)
  textPlaceholder?: string;  // Placeholder text
  textMinLength?: number;    // Minimum characters
  textMaxLength?: number;    // Maximum characters
  
  // Existing fields we need to preserve
  category?: string;         // For scoring
  reversed?: boolean;        // For reverse scoring
}

/**
 * Survey configuration with enhanced questions
 */
export interface EnhancedSurvey {
  id: number;
  name: string;
  type: 'pulse' | 'shape';
  questions: EnhancedSurveyQuestion[];
}

/**
 * API response for survey questions
 */
export interface SurveyQuestionsResponse {
  questions: EnhancedSurveyQuestion[];
  totalQuestions: number;
}

/**
 * Maps backend response to frontend format
 * Handles camelCase conversion and field mapping
 */
export function mapApiQuestionToEnhanced(apiQuestion: any): EnhancedSurveyQuestion {
  // Map responseType from backend format
  let responseType: 'scale' | 'text' = 'scale';
  if (apiQuestion.responseType === 'text' || apiQuestion.response_type === 'text') {
    responseType = 'text';
  } else if (apiQuestion.responseType === 'numeric' || apiQuestion.response_type === 'numeric') {
    responseType = 'scale';
  }
  
  return {
    id: apiQuestion.id,
    surveyId: apiQuestion.surveyId || apiQuestion.survey_id || 1,
    text: apiQuestion.question || apiQuestion.text || apiQuestion.questionText || apiQuestion.question_text,
    order: apiQuestion.order,
    responseType,
    
    // Scale fields
    scaleMin: apiQuestion.scaleMin || apiQuestion.scale_min,
    scaleMax: apiQuestion.scaleMax || apiQuestion.scale_max,
    scaleMinLabel: apiQuestion.scaleMinLabel || apiQuestion.scale_min_label,
    scaleMaxLabel: apiQuestion.scaleMaxLabel || apiQuestion.scale_max_label,
    
    // Text fields
    textPlaceholder: apiQuestion.textPlaceholder || apiQuestion.text_placeholder,
    textMinLength: apiQuestion.textMinLength || apiQuestion.text_min_length,
    textMaxLength: apiQuestion.textMaxLength || apiQuestion.text_max_length,
    
    // Existing fields
    category: apiQuestion.category || apiQuestion.categoryId?.toString(),
    reversed: apiQuestion.reversed || apiQuestion.isNegative,
  };
}

/**
 * Standard scale configuration (hardcoded)
 * All scale questions use 1-5 with these labels
 */
export const STANDARD_SCALE_CONFIG = {
  range: [1, 2, 3, 4, 5] as const,
  labels: ['Disagree', 'Somewhat disagree', 'Neutral', 'Somewhat agree', 'Agree'] as const,
};